/**
 * Relaxed label matching for searchable selects.
 *
 * Spreadsheet headers and typed queries rarely share punctuation, spacing, or
 * exact spelling ("firstname" vs "First Name", "emial" vs "Email"). Rank by
 * how tight the match is so a compact/prefix hit sorts above a subsequence.
 */

export function normalizeForRelaxedSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function compact(value: string): string {
  return value.replace(/\s+/g, '');
}

function isSubsequence(needle: string, haystack: string): boolean {
  let index = 0;
  for (const character of haystack) {
    if (character === needle[index]) index += 1;
    if (index === needle.length) return true;
  }
  return false;
}

/** Adjacent transpose, one insert/delete, or one substitute. */
function damerauDistanceAtMostOne(left: string, right: string): boolean {
  if (left === right) return true;
  const leftLength = left.length;
  const rightLength = right.length;
  if (Math.abs(leftLength - rightLength) > 1) return false;

  if (leftLength === rightLength) {
    let diffs = 0;
    let first = -1;
    for (let i = 0; i < leftLength; i += 1) {
      if (left[i] === right[i]) continue;
      diffs += 1;
      if (diffs === 1) {
        first = i;
      } else if (diffs === 2) {
        if (!(i === first + 1 && left[first] === right[i] && left[i] === right[first])) {
          return false;
        }
      } else {
        return false;
      }
    }
    return diffs <= 2;
  }

  const [shorter, longer] = leftLength < rightLength ? [left, right] : [right, left];
  let skipped = false;
  let shortIndex = 0;
  let longIndex = 0;
  while (shortIndex < shorter.length && longIndex < longer.length) {
    if (shorter[shortIndex] === longer[longIndex]) {
      shortIndex += 1;
      longIndex += 1;
      continue;
    }
    if (skipped) return false;
    skipped = true;
    longIndex += 1;
  }
  return true;
}

function tokenMatches(queryToken: string, labelToken: string): boolean {
  if (labelToken.includes(queryToken) || queryToken.includes(labelToken)) return true;
  return queryToken.length >= 4 && damerauDistanceAtMostOne(queryToken, labelToken);
}

/**
 * Lower is tighter. `null` means no match.
 * 0 exact compact, 1 prefix, 2 compact substring, 3 token, 4 subsequence.
 */
export function relaxedSearchScore(query: string, label: string): number | null {
  const normalizedQuery = normalizeForRelaxedSearch(query);
  if (!normalizedQuery) return 0;

  const normalizedLabel = normalizeForRelaxedSearch(label);
  if (!normalizedLabel) return null;

  const compactQuery = compact(normalizedQuery);
  const compactLabel = compact(normalizedLabel);

  if (compactLabel === compactQuery) return 0;
  if (compactLabel.startsWith(compactQuery)) return 1;
  if (compactLabel.includes(compactQuery)) return 2;

  const queryTokens = normalizedQuery.split(/\s+/);
  const labelTokens = normalizedLabel.split(/\s+/);
  const tokensHit = queryTokens.every((queryToken) =>
    labelTokens.some((labelToken) => tokenMatches(queryToken, labelToken)),
  );
  if (tokensHit) return 3;

  if (compactQuery.length >= 2 && isSubsequence(compactQuery, compactLabel)) return 4;
  return null;
}

export function rankByRelaxedSearch<T>(
  items: T[],
  query: string,
  getLabel: (item: T) => string,
): T[] {
  const trimmed = query.trim();
  if (!trimmed) return items;

  const ranked: { item: T; score: number }[] = [];
  for (const item of items) {
    const score = relaxedSearchScore(trimmed, getLabel(item));
    if (score === null) continue;
    ranked.push({ item, score });
  }
  ranked.sort((left, right) => left.score - right.score);
  return ranked.map((entry) => entry.item);
}
